import { ApolloQueryResult } from '@apollo/client';
import { Maybe } from 'graphql/jsutils/Maybe';
import React from 'react';
import { useTranslation } from 'react-i18next';
import SectionError from '../../Shared/SectionError';
import GenericExploreContent from '../GenericExploreContent/GenericExploreContent';
import { Series, Tool } from '../types';
import {
  Container,
  SectionHeadingTitle,
  SectionTitleSkeletonPlaceholder,
} from '../ExplorePage';
import { exploreFeaturedContent } from '../__generated__/exploreFeaturedContent';
import GenericExploreContentSkeleton from '../GenericExploreContent/GenericExploreContentSkeleton';

export interface FeaturedContentProps {
  content: Maybe<(Series | Tool)[]>;
  assetToken?: string | null;
  hasErrored: boolean;
  refetch(): Promise<ApolloQueryResult<exploreFeaturedContent>>;
  isLoading: boolean;
}

function FeaturedContent({
  content,
  isLoading,
  assetToken,
  hasErrored,
  refetch,
}: FeaturedContentProps) {
  const { t: translate } = useTranslation('explore');

  const SectionTitle = () => (
    <SectionHeadingTitle data-testid="featured-content-title">
      {translate('featured_content_section.title')}
    </SectionHeadingTitle>
  );

  if (isLoading && !content?.length) {
    return (
      <Container>
        <SectionTitleSkeletonPlaceholder
          title={translate('featured_content_section.title')}
        />
        <GenericExploreContentSkeleton />
      </Container>
    );
  }

  if (hasErrored) {
    return (
      <Container>
        <SectionTitle />
        <SectionError onRetry={refetch} />
      </Container>
    );
  }

  return content?.length ? (
    <Container>
      <SectionTitle />
      <GenericExploreContent
        content={content}
        assetToken={assetToken}
        source="explore-featured-content"
      />
    </Container>
  ) : null;
}

export default FeaturedContent;
